import * as CoreFrameDTOs from "coreframetypings/dto";
import type DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import Utilities from '@/Utility/Utilities';
import type ControlFactory from '@/Factories/ControlFactory';
import type ListViewViewModel from '@/ViewModels/Views/ListViewViewModel';
import type ModalDetailViewContext from '@/Components/ModalDetailView/ModalDetailViewContext';
import type EditContext from '@/ViewModels/Contexts/EditContext';
import DetailViewDTOAccessor from '@/Utility/DetailViewDTOAccessor';

export default class UIListenerHelper {
    constructor() {}
    
    public static isDetailViewContext(context: EditContext | ListViewViewModel): context is DetailViewContext {
        let isDetailViewContext = context.type.startsWith('DetailView') || UIListenerHelper.isModalDetailViewContext(context);

        return isDetailViewContext;
    }

    public static isModalDetailViewContext(context: EditContext | ListViewViewModel): context is ModalDetailViewContext {
        return context.type.startsWith('ModalDetailView');
    }

    public static isListViewContext(context: DetailViewContext | ListViewViewModel): context is ListViewViewModel {
        return !UIListenerHelper.isDetailViewContext(context);
    }
    
    /**
     * Entfernt ein IDVGItemDTO anhand des Namens
     * @param context
     * @param name
     */
    public static removeDetailViewItemDto(context: DetailViewContext, name: string): void {
        if (!UIListenerHelper.isDetailViewContext(context))
            return;

        const dvc = context as DetailViewContext;

        if (Utilities.isNullOrUndefined(dvc) ||
            Utilities.isNullOrUndefined(dvc.detailViewContextDTO) ||
            Utilities.isNullOrUndefined(dvc.detailViewContextDTO.detailView))
            return;

        DetailViewDTOAccessor.removeDetailViewItemDtoInDetailView(dvc.detailViewContextDTO.detailView, name);

        context.collectAllItems();
    }

    /**
     * Entfernt ein IDetailViewGroupDTO anhand des Namens
     * @param context
     * @param name
     */
    public static removeDetailViewGroupDto(context: DetailViewContext, name: string): void {
        if (!UIListenerHelper.isDetailViewContext(context))
            return;

        const dvc = context as DetailViewContext;

        if (Utilities.isNullOrUndefined(dvc) ||
            Utilities.isNullOrUndefined(dvc.detailViewContextDTO) ||
            Utilities.isNullOrUndefined(dvc.detailViewContextDTO.detailView))
            return;

        DetailViewDTOAccessor.removeDetailViewGroupDtoInDetailView(dvc.detailViewContextDTO.detailView, name);

        context.collectAllItems();
    }

    /**
     * Liefert ein IDetailViewGroupDTO anhand des Namens
     * @param context
     * @param name
     */
    public static getDetailViewGroupDto(context: DetailViewContext, name: string): CoreFrameDTOs.IDetailViewGroupDTO {
        if (!this.isDetailViewContext(context))
            return null;

        const dvc = context as DetailViewContext;

        if (Utilities.isNullOrUndefined(dvc) ||
            Utilities.isNullOrUndefined(dvc.detailViewContextDTO) ||
            Utilities.isNullOrUndefined(dvc.detailViewContextDTO.detailView))
            return null;

        return DetailViewDTOAccessor.findDetailViewGroupDtoInDetailView(context.detailViewContextDTO.detailView, name);
    }

    /**
     * Liefert ein IDVGItemDTO anhand des Namens
     * @param context
     * @param name
     */
    public static getDetailViewItemDto(context: EditContext, name: string): CoreFrameDTOs.IDVGItemDTO {
        if (!this.isDetailViewContext(context))
            return null;

        const dvc = context as DetailViewContext;

        if (Utilities.isNullOrUndefined(dvc) ||
            Utilities.isNullOrUndefined(dvc.detailViewContextDTO) ||
            Utilities.isNullOrUndefined(dvc.detailViewContextDTO.detailView))
            return null;

        return DetailViewDTOAccessor.findDetailViewItemDtoInDetailView(dvc.detailViewContextDTO.detailView, name);
    }

    /**
     * Liefert alle ControlFactories innerhalb einer Gruppe zurück
     * @param context
     * @param dvGroupName
     * @param subGroupsInCluded
     */
    public static getUiFactoriesInDetailViewGroup(context: DetailViewContext, dvGroupName: string, subGroupsInCluded: boolean) {
        let factories: Array<ControlFactory> = [];
        const dvGroup = this.getDetailViewGroupDto(context, dvGroupName);

        factories = dvGroup.items.map(i => context.getUIFactory(i.name));

        if (subGroupsInCluded)
            dvGroup.detailViewGroups.forEach(g => factories = factories.concat(this.getUiFactoriesInDetailViewGroup(context, g.name, subGroupsInCluded)));

        return factories;
    }

    /**
     * Aktiviert alle ControlFactories innerhalb einer Gruppe
     * @param context
     * @param dvGroupName
     * @param subGroupsInCluded
     */
    public static enableUiFactoriesInDetailViewGroup(context: DetailViewContext,
        dvGroupName: string,
        subGroupsInCluded: boolean): void {
        this.getUiFactoriesInDetailViewGroup(context, dvGroupName, subGroupsInCluded).forEach(g => g.isEnabled(true));
    }

    /**
     * Deaktiviert alle ControlFactories innerhalb einer Gruppe
     * @param context
     * @param dvGroupName
     * @param subGroupsInCluded
     */
    public static disableUiFactoriesInDetailViewGroup(context: DetailViewContext,
        dvGroupName: string,
        subGroupsInCluded: boolean): void {
        this.getUiFactoriesInDetailViewGroup(context, dvGroupName, subGroupsInCluded).forEach(g => g.isEnabled(false));
    }
}